import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { authorizeAudio } from "../lib/api";
import { track as trackEvent } from "../lib/analytics";

const NOTICES = {
  LOCKED: { message: "This signal is not yet available." },
  SCHEDULED: { message: "This signal has not yet arrived." },
  PURCHASE_REQUIRED: { message: "Own the album to listen to this track.", cta: { label: "GET THE ALBUM", to: "/store" } },
  INNER_CIRCLE: { message: "Reserved for Inner Circle members.", cta: { label: "JOIN THE INNER CIRCLE", to: "/inner-circle" } },
  VAULT_ENTITLEMENT: { message: "This track requires a vault entitlement.", cta: { label: "OPEN MY VAULT", to: "/account" } },
};

export const PLAYABLE = ["FULL_PUBLIC", "TEASER", "PREVIEW", "PURCHASE_REQUIRED", "INNER_CIRCLE", "VAULT_ENTITLEMENT"];
export const REPEAT_MODES = ["off", "track", "queue"];

const PREFS_KEY = "mm_player_prefs_v1";
const STATE_KEY = "mm_player_state_v1";

const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
};

/* keep only what's needed to re-resolve playback later (never a protected src) */
const slimTrack = (t) => ({
  title: t.title, trackNumber: t.trackNumber, slug: t.slug, availability: t.availability,
  streamUrl: t.streamUrl || null, teaserUrl: t.teaserUrl || null, previewUrl: t.previewUrl || null,
  teaserDuration: t.teaserDuration || null, previewDuration: t.previewDuration || null,
  duration: t.duration || null, artwork: t.artwork || null, lockLabel: t.lockLabel || null,
});

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const initialPrefs = useRef(loadPrefs());
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(!!initialPrefs.current.muted);
  const [volume, setVolumeState] = useState(
    typeof initialPrefs.current.volume === "number" ? initialPrefs.current.volume : 1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [notice, setNotice] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [queue, setQueue] = useState([]);
  const [qIndex, setQIndex] = useState(-1);
  const [shuffle, setShuffle] = useState(!!initialPrefs.current.shuffle);
  const [repeat, setRepeat] = useState(
    REPEAT_MODES.includes(initialPrefs.current.repeat) ? initialPrefs.current.repeat : "off");

  const pendingSeekRef = useRef(0);
  const lastPersistRef = useRef(0);
  const queueRef = useRef(queue); queueRef.current = queue;
  const qIndexRef = useRef(qIndex); qIndexRef.current = qIndex;
  const currentRef = useRef(current); currentRef.current = current;
  const shuffleRef = useRef(shuffle); shuffleRef.current = shuffle;
  const repeatRef = useRef(repeat); repeatRef.current = repeat;
  const mutedRef = useRef(muted); mutedRef.current = muted;
  const volumeRef = useRef(volume); volumeRef.current = volume;

  /* ---------- persistence ---------- */
  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ volume, muted, shuffle, repeat }));
    } catch {}
  }, [volume, muted, shuffle, repeat]);

  const persistState = useCallback(() => {
    try {
      const list = queueRef.current;
      if (!currentRef.current || !list.length) { localStorage.removeItem(STATE_KEY); return; }
      localStorage.setItem(STATE_KEY, JSON.stringify({
        queue: list.map((e) => ({ t: slimTrack(e.t), ctx: e.ctx || {} })),
        qIndex: qIndexRef.current,
        position: audioRef.current?.currentTime || 0,
      }));
    } catch {}
  }, []);

  /* ---------- media session ---------- */
  const setMediaMeta = useCallback((data) => {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: data.title || "",
        artist: "Motion & Meaning",
        album: data.releaseTitle || "",
        artwork: data.artwork ? [{ src: data.artwork, sizes: "512x512", type: "image/jpeg" }] : [],
      });
    } catch {}
  }, []);

  /* ---------- core loading ---------- */
  const load = useCallback((data) => {
    setCurrent(data);
    setNotice(null);
    setProgress(0);
    setDuration(0);
    const a = audioRef.current;
    if (a) {
      a.src = data.src;
      a.muted = mutedRef.current;
      a.volume = volumeRef.current;
      a.play().catch(() => {});
    }
    setMediaMeta(data);
  }, [setMediaMeta]);

  const resolveSrc = useCallback(async (t, ctx = {}) => {
    if (t.availability === "FULL_PUBLIC" && t.streamUrl) return { src: t.streamUrl };
    if ((t.availability === "TEASER" || t.availability === "PREVIEW") && (t.teaserUrl || t.previewUrl)) {
      return {
        src: t.teaserUrl || t.previewUrl, teaser: true,
        teaserDuration: t.teaserDuration || t.previewDuration || null,
        previewDuration: t.availability === "PREVIEW" ? t.previewDuration || null : null,
      };
    }
    if (["PURCHASE_REQUIRED", "INNER_CIRCLE", "VAULT_ENTITLEMENT"].includes(t.availability)) {
      try {
        const r = await authorizeAudio({ slug: ctx.releaseSlug, trackNumber: t.trackNumber });
        return { src: r.stream_path };
      } catch (err) { return { err }; }
    }
    return { locked: true };
  }, []);

  const buildBase = (t, ctx = {}) => ({
    title: t.title, trackNumber: t.trackNumber, trackSlug: t.slug,
    artwork: t.artwork || ctx.artwork || null,
    releaseSlug: ctx.releaseSlug || null, releaseTitle: ctx.releaseTitle || null,
    dimension: ctx.dimension || null,
  });

  const playEntryAt = useCallback(async (idx, list) => {
    const entries = list || queueRef.current;
    const entry = entries[idx];
    if (!entry) return false;
    const { t, ctx } = entry;
    const res = await resolveSrc(t, ctx || {});
    if (res.src) {
      setQIndex(idx);
      load({ ...buildBase(t, ctx || {}), src: res.src, teaser: !!res.teaser,
             teaserDuration: res.teaserDuration || null, previewDuration: res.previewDuration || null });
      return true;
    }
    if (res.err) {
      if (res.err.response?.status === 401) {
        setNotice({ title: t.title, message: "Sign in to verify your access to this track.",
          cta: { label: "SIGN IN", to: `/login?next=${window.location.pathname}` } });
      } else {
        setNotice({ title: t.title, ...(NOTICES[t.availability] || NOTICES.LOCKED) });
      }
    } else {
      setNotice({ title: t.title, ...(NOTICES[t.availability] || NOTICES.LOCKED) });
    }
    return false;
  }, [load, resolveSrc]);

  /* ---------- public API ---------- */
  const playTrack = useCallback(async (t, ctx = {}) => {
    setNotice(null);
    trackEvent(ctx.source === "hero" ? "hero_play" : "track_play",
      { title: t.title, dimension: ctx.dimension || "" });
    const baseCtx = { releaseSlug: ctx.releaseSlug || null, releaseTitle: ctx.releaseTitle || null,
                      artwork: ctx.artwork || null, dimension: ctx.dimension || null };
    if (!PLAYABLE.includes(t.availability)) {
      setNotice({ title: t.title, ...(NOTICES[t.availability] || NOTICES.LOCKED) });
      return false;
    }
    let entries;
    if (Array.isArray(ctx.queue) && ctx.queue.length) {
      entries = ctx.queue.filter((x) => PLAYABLE.includes(x.availability))
        .map((x) => ({ t: slimTrack(x), ctx: baseCtx }));
    } else {
      entries = [{ t: slimTrack(t), ctx: baseCtx }];
    }
    let idx = entries.findIndex((e) => e.t.trackNumber === t.trackNumber && e.t.slug === t.slug);
    if (idx === -1) { entries.unshift({ t: slimTrack(t), ctx: baseCtx }); idx = 0; }
    setQueue(entries);
    const ok = await playEntryAt(idx, entries);
    if (ok) setTimeout(persistState, 0);
    return ok;
  }, [playEntryAt, persistState]);

  const playDirect = useCallback((data) => {
    setNotice(null);
    setQueue([]);
    setQIndex(-1);
    try { localStorage.removeItem(STATE_KEY); } catch {}
    load(data);
  }, [load]);

  const playQueueIndex = useCallback((i) => { playEntryAt(i); }, [playEntryAt]);

  const advance = useCallback(async (dir = 1, auto = false) => {
    const list = queueRef.current;
    if (!list.length) return;
    let i = qIndexRef.current;
    if (shuffleRef.current && list.length > 1) {
      let n = i;
      while (n === i) n = Math.floor(Math.random() * list.length);
      i = n;
    } else {
      i += dir;
      if (i >= list.length) {
        if (repeatRef.current === "queue" || !auto) i = 0;
        else { setPlaying(false); return; }
      }
      if (i < 0) i = list.length - 1;
    }
    await playEntryAt(i);
    persistState();
  }, [playEntryAt, persistState]);

  const next = useCallback(() => advance(1, false), [advance]);
  const prev = useCallback(() => {
    const a = audioRef.current;
    if (a && a.currentTime > 4) { a.currentTime = 0; return; }
    advance(-1, false);
  }, [advance]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    const c = currentRef.current;
    if (!a || !c) return;
    if (!a.src || c.restored) {
      playEntryAt(qIndexRef.current);
      return;
    }
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }, [playEntryAt]);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    setProgress(0);
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const nextVal = !m;
      if (audioRef.current) audioRef.current.muted = nextVal;
      return nextVal;
    });
  }, []);

  const setVolume = useCallback((v) => {
    const clamped = Math.min(1, Math.max(0, v));
    setVolumeState(clamped);
    if (audioRef.current) audioRef.current.volume = clamped;
  }, []);

  const seek = useCallback((v) => {
    if (audioRef.current) audioRef.current.currentTime = v;
  }, []);

  const close = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute("src"); }
    setCurrent(null);
    setPlaying(false);
    setProgress(0);
    setDuration(0);
    setQueue([]);
    setQIndex(-1);
    setExpanded(false);
    try { localStorage.removeItem(STATE_KEY); } catch {}
  }, []);

  /* ---------- queue ops (also the future saved-playlist loading surface) ---------- */
  const addToQueue = useCallback((t, ctx = {}) => {
    if (!PLAYABLE.includes(t.availability)) return false;
    const entry = { t: slimTrack(t), ctx: { releaseSlug: ctx.releaseSlug || null,
      releaseTitle: ctx.releaseTitle || null, artwork: ctx.artwork || null, dimension: ctx.dimension || null } };
    setQueue((qs) => [...qs, entry]);
    setTimeout(persistState, 0);
    return true;
  }, [persistState]);

  const playNext = useCallback((t, ctx = {}) => {
    if (!PLAYABLE.includes(t.availability)) return false;
    const entry = { t: slimTrack(t), ctx: { releaseSlug: ctx.releaseSlug || null,
      releaseTitle: ctx.releaseTitle || null, artwork: ctx.artwork || null, dimension: ctx.dimension || null } };
    setQueue((qs) => {
      const i = qIndexRef.current;
      return [...qs.slice(0, i + 1), entry, ...qs.slice(i + 1)];
    });
    setTimeout(persistState, 0);
    return true;
  }, [persistState]);

  const removeFromQueue = useCallback((i) => {
    if (i === qIndexRef.current) return;
    setQueue((qs) => qs.filter((_, j) => j !== i));
    if (i < qIndexRef.current) setQIndex((x) => x - 1);
    setTimeout(persistState, 0);
  }, [persistState]);

  const moveInQueue = useCallback((i, dir) => {
    setQueue((qs) => {
      const j = i + dir;
      if (j < 0 || j >= qs.length) return qs;
      const copy = [...qs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      const cur = qIndexRef.current;
      if (cur === i) setQIndex(j);
      else if (cur === j) setQIndex(i);
      return copy;
    });
    setTimeout(persistState, 0);
  }, [persistState]);

  const toggleShuffle = useCallback(() => setShuffle((s) => !s), []);
  const cycleRepeat = useCallback(() =>
    setRepeat((r) => REPEAT_MODES[(REPEAT_MODES.indexOf(r) + 1) % REPEAT_MODES.length]), []);

  const isCurrent = useCallback((releaseSlug, trackNumber) =>
    !!current && current.releaseSlug === releaseSlug && current.trackNumber === trackNumber,
  [current]);

  /* ---------- audio events ---------- */
  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setProgress(a.currentTime);
    const d = currentRef.current?.previewDuration
      ? Math.min(a.duration || 0, currentRef.current.previewDuration)
      : a.duration || 0;
    setDuration(Number.isFinite(d) ? d : 0);
    if (currentRef.current?.previewDuration && a.currentTime >= currentRef.current.previewDuration) {
      if (queueRef.current.length > 1 || repeatRef.current !== "off") {
        onEnded();
      } else {
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
      }
      return;
    }
    if ("mediaSession" in navigator && Number.isFinite(a.duration) && a.duration > 0) {
      try {
        navigator.mediaSession.setPositionState({
          duration: a.duration, playbackRate: a.playbackRate, position: Math.min(a.currentTime, a.duration) });
      } catch {}
    }
    const now = Date.now();
    if (now - lastPersistRef.current > 5000) { lastPersistRef.current = now; persistState(); }
  };

  const onLoadedMeta = () => {
    const a = audioRef.current;
    if (a && pendingSeekRef.current > 0) {
      try { a.currentTime = pendingSeekRef.current; } catch {}
      pendingSeekRef.current = 0;
    }
    onTime();
  };

  const onEnded = () => {
    const a = audioRef.current;
    if (repeatRef.current === "track" && a) {
      a.currentTime = 0;
      a.play().catch(() => {});
      return;
    }
    if (queueRef.current.length > 1 || repeatRef.current === "queue") advance(1, true);
    else setPlaying(false);
  };

  /* ---------- restore on mount (metadata only — never autoplays) ---------- */
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STATE_KEY));
      if (s && Array.isArray(s.queue) && s.queue.length && s.qIndex >= 0 && s.queue[s.qIndex]) {
        setQueue(s.queue);
        setQIndex(s.qIndex);
        const { t, ctx } = s.queue[s.qIndex];
        setCurrent({ ...buildBase(t, ctx || {}), restored: true });
        setProgress(s.position || 0);
        setMinimized(true);
        pendingSeekRef.current = s.position || 0;
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- media session handlers ---------- */
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    const safe = (action, fn) => { try { ms.setActionHandler(action, fn); } catch {} };
    safe("play", () => { audioRef.current?.play().catch(() => {}); });
    safe("pause", () => { audioRef.current?.pause(); });
    safe("previoustrack", () => prev());
    safe("nexttrack", () => next());
    safe("stop", () => stop());
    safe("seekto", (d) => { if (audioRef.current && d.seekTime != null) audioRef.current.currentTime = d.seekTime; });
    return () => ["play", "pause", "previoustrack", "nexttrack", "stop", "seekto"].forEach((x) => safe(x, null));
  }, [next, prev, stop]);

  useEffect(() => {
    const onUnload = () => persistState();
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [persistState]);

  return (
    <PlayerCtx.Provider value={{
      current, playing, muted, volume, progress, duration, notice, minimized, expanded,
      queue, qIndex, shuffle, repeat,
      playTrack, playDirect, playQueueIndex, toggle, stop, toggleMute, setVolume, seek, close,
      next, prev, addToQueue, playNext, removeFromQueue, moveInQueue, toggleShuffle, cycleRepeat,
      setNotice, setMinimized, setExpanded, isCurrent,
    }}>
      <audio ref={audioRef} onTimeUpdate={onTime} onLoadedMetadata={onLoadedMeta}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={onEnded} preload="none" />
      {children}
    </PlayerCtx.Provider>
  );
}
